import { Component, Input, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HighchartsChartModule } from 'highcharts-angular';
import * as Highcharts from 'highcharts';
import { MemberService } from '../../member-details/member-service/MemberService ';
import { MemberDashboardService } from '../member-dashboard.service';
import moment from 'moment';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
declare var $: any;

@Component({
  selector: 'app-combined-metrics',
  standalone: true,
  imports: [CommonModule, FormsModule, HighchartsChartModule, MatProgressSpinnerModule],
  templateUrl: './combined-metrics.component.html',
  styleUrl: './combined-metrics.component.css'
})
export class CombinedMetricsComponent {
  @Input() sDate: string | undefined;
  @Input() eDate: string | undefined;
  @Input() memberId: string | undefined;
  @Input() memberbasic: any;
  @ViewChild('chart') chart: any;

  Highcharts: typeof Highcharts = Highcharts;
  isLoading: number = 1;
  noDataFound: boolean = false;
  emptyMessage: string = '';

  // Selected metrics (HR, RHR, HRV only)
  selectedMetrics: { [key: string]: boolean } = {
    'hr': true,    // Heart Rate (default selected)
    'rhr': true,   // Resting Heart Rate (default selected)
    'hrv': false   // HRV
  };

  private chartInstance: any;
  updateFlag: boolean = false;
  oneToOneFlag: boolean = true;
  runOutsideAngular: boolean = false;
  showChart: boolean = true;

  chartOptions: Highcharts.Options = {};
  metricsData: any = {
    heartrate: [],
    rhr: [],
    hrv: []
  };
  timeLineData: any[] = [];
  timeLines: any[] = [];

  // Modal properties
  showModal: boolean = false;
  modalType: string = '';
  modalSTime: string = '';
  modalETime: string = '';
  modalDuration: string = '';
  modalImg: string = '';
  activityName: string = '';
  calsBurned: number = 0;
  distCovered: string = '';
  distUnits: string = 'Km';

  constructor(private memberService: MemberService, private dashboardService: MemberDashboardService) {}

  ngOnChanges(changes: SimpleChanges) {
    if ((changes['sDate'] || changes['eDate'] || changes['memberId']) && this.memberId && this.sDate && this.eDate) {
      this.loadMetrics(this.sDate, this.eDate, this.memberId);
    }
  }

  onMetricToggle(metric: string) {
    // Show loading spinner to prevent layout flicker
    this.isLoading = 1;
    
    // Hide chart first
    this.showChart = false;
    
    // Create new chart options
    this.createCombinedGraph();
    
    // Show chart after options are set (use setTimeout to ensure Angular detects the change)
    setTimeout(() => {
      this.showChart = true;
    }, 50);
  }

  selectAll() {
    Object.keys(this.selectedMetrics).forEach(k => this.selectedMetrics[k] = true);
    this.onMetricToggle('');
  }

  clearAll() {
    Object.keys(this.selectedMetrics).forEach(k => this.selectedMetrics[k] = false);
    this.onMetricToggle('');
  }

  getSelectedMetrics(): string[] {
    return Object.keys(this.selectedMetrics).filter(key => this.selectedMetrics[key]);
  }

  private async loadMetrics(start: string, end: string, memberId: string) {
    this.isLoading = 1;
    const dates = this.enumerateDates(start, end).map(d => formatDate(d, 'yyyy-MM-dd', 'en'));

    // Initialize metrics data structure
    this.metricsData = {
      heartrate: [],
      rhr: [],
      hrv: []
    };
    this.timeLineData = [];
    this.timeLines = [];
    
    try {
      // use formatted start/end (dates array already contains yyyy-MM-dd strings with leading zeros)
      const formattedStart = dates[0];
      const formattedEnd = dates[dates.length - 1];
      const resp: any = await this.dashboardService.getHealthMetrics(memberId, formattedStart, formattedEnd).toPromise();
      
      // Handle new API response structure: { healthMetrics: { heartrate: [...], rhr: [...], hrv: [...] }, timeLine: [...] }
      if (resp?.healthMetrics) {
        // Helper function to parse date string without timezone conversion
        // Since API date is already in member's timezone, parse it as local time
        const parseDateAsLocal = (dateStr: string): number => {
          // Parse the date string as local time (no timezone conversion)
          // Format: "2026-01-05T13:04:19" or "2026-01-05T13:04:19.000"
          const date = new Date(dateStr);
          return date.getTime();
        };

        // Extract heartrate data
        if (resp.healthMetrics.heartrate && Array.isArray(resp.healthMetrics.heartrate)) {
          this.metricsData.heartrate = resp.healthMetrics.heartrate.map((item: any) => {
            const timestamp = parseDateAsLocal(item.date);
            return {
              date: item.date,
              value: item.value,
              timestamp: timestamp
            };
          });
        }
        
        // Extract rhr (resting heart rate) data
        if (resp.healthMetrics.rhr && Array.isArray(resp.healthMetrics.rhr)) {
          this.metricsData.rhr = resp.healthMetrics.rhr.map((item: any) => {
            const timestamp = parseDateAsLocal(item.date);
            return {
              date: item.date,
              value: item.value,
              timestamp: timestamp
            };
          });
        }
        
        // Extract hrv data
        if (resp.healthMetrics.hrv && Array.isArray(resp.healthMetrics.hrv)) {
          this.metricsData.hrv = resp.healthMetrics.hrv.map((item: any) => {
            const timestamp = parseDateAsLocal(item.date);
            return {
              date: item.date,
              value: item.value,
              timestamp: timestamp
            };
          });
        }
      }
      
      // Extract timeline data
      if (resp?.timeLine && Array.isArray(resp.timeLine)) {
        this.timeLineData = resp.timeLine;
      } else if (resp?.timeline && Array.isArray(resp.timeline)) {
        this.timeLineData = resp.timeline;
      }
    } catch (err) {
      // If aggregated API fails, fall back to empty metrics
      this.metricsData = {
        heartrate: [],
        rhr: [],
        hrv: []
      };
    }

    // Force chart recreation
    this.forceChartRecreation();
    setTimeout(() => {
      this.createCombinedGraph();
    }, 150);
  }

  private createCombinedGraph() {
    const selectedMetrics = this.getSelectedMetrics();

    // Check if we have timeline data (icons) even when no metrics are selected
    const hasTimelineData = this.timeLineData && this.timeLineData.length > 0;
    
    // Collect all data points from selected metrics
    const allDataPoints: any[] = [];
    let allValues: number[] = [];

    // Map metric keys to data properties and colors
    const metricConfig: { [key: string]: { dataKey: string; name: string; color: string; yAxis: string } } = {
      'hr': { dataKey: 'heartrate', name: 'Heart Rate', color: '#FF5733', yAxis: 'Heart Rate (BPM)' },
      'rhr': { dataKey: 'rhr', name: 'Resting Heart Rate', color: '#3366CC', yAxis: 'Resting HR (BPM)' },
      'hrv': { dataKey: 'hrv', name: 'HRV', color: '#33CC33', yAxis: 'HRV (ms)' }
    };

    // Prepare series data
    const seriesData: any[] = [];

    // Process each selected metric
    selectedMetrics.forEach((metric: string) => {
      const config = metricConfig[metric];
      if (!config) return;

      const metricData = this.metricsData[config.dataKey] || [];
      if (metricData.length === 0) return;

      // Convert to Highcharts format: [timestamp, value]
      const data = metricData.map((item: any) => {
        if (item.value !== null && item.value !== undefined) {
          allValues.push(item.value);
          const point = [item.timestamp, item.value];
          return point;
        }
        return null;
      }).filter((point: any) => point !== null);

      if (data.length > 0) {
      seriesData.push({
        name: config.name,
        data: data,
        type: 'line',
        color: config.color,
        showInLegend: true,
        marker: {
          enabled: false
        }
      });
      }
    });

    // Y-axis bounds: dynamic based on SELECTED metrics' data only
    // Scale and tick marks update when user toggles checkboxes or data changes
    let yMin = 0;
    let yMax = 100;

    if (allValues.length > 0) {
      const minValue = Math.min(...allValues);
      const maxValue = Math.max(...allValues);
      const range = maxValue - minValue;
      const padding = Math.max(5, range * 0.1);
      yMin = Math.max(0, Math.floor((minValue - padding) / 10) * 10);
      yMax = Math.ceil((maxValue + padding) / 10) * 10;
    }

    // Check if we have any metric data (from any metric, for empty-state logic)
    const allMetricValues: number[] = [];
    Object.keys(metricConfig).forEach((metric: string) => {
      const config = metricConfig[metric];
      if (!config) return;
      const metricData = this.metricsData[config.dataKey] || [];
      metricData.forEach((item: any) => {
        if (item.value !== null && item.value !== undefined) {
          allMetricValues.push(item.value);
        }
      });
    });
    const hasMetricData = allMetricValues.length > 0;

    // If no metrics selected and no timeline data, show message
    if (selectedMetrics.length === 0 && !hasTimelineData) {
      this.noDataFound = true;
      this.setEmptyChart();
      this.emptyMessage = 'Please select at least one metric to view data.';
      this.isLoading = 2;
      return;
    }

    // If no metric data and no timeline data, show empty state
    if (!hasMetricData && !hasTimelineData) {
      this.noDataFound = true;
      this.setEmptyChart();
      this.emptyMessage = 'No data available for selected date range.';
      this.isLoading = 2;
      return;
    }

    this.noDataFound = false;

    // Process timeline data for icons and background ranges (sleep/activity) - use exact timestamps
    const sleepPoints: any[] = [];
    const workoutPoints: any[] = [];
    const otherTimelinePoints: any[] = [];
    const plotBands: any[] = []; // background color bands for ranges
    const sleepBandColor = 'rgba(222, 48, 255, 0.1)';    // #DE30FF at 10% opacity
    const activityBandColor = 'rgba(0, 107, 242, 0.1)';  // #006BF2 at 10% opacity
    const baseYValue = yMin + (yMax - yMin) * 0.1; // Position icons near the bottom of the chart
    
    if (this.timeLineData && this.timeLineData.length > 0) {
      this.timeLineData.forEach((timeline: any) => {
        if (!timeline.date) return;
        
        // Parse date as local time (no timezone conversion)
        const date = new Date(timeline.date);
        const timestamp = date.getTime();
        
        const normalizedType = (timeline.type || '').toString().trim().toLowerCase();
        
        const timelinePoint: any = {
          x: timestamp,
          y: baseYValue,
          type: timeline.type,
          data: timeline.data,
          events: {
            click: (e: any) => {
              this.showInfo(e.point.type, e.point.data);
            },
          },
          marker: {
            height: 25,
            width: 25,
            symbol: '',
          },
        };
        
        // Add background range plotBand for sleep/activity when startTime & endTime are present
        const startTime = timeline.data?.startTime;
        const endTime = timeline.data?.endTime;
        if (startTime && endTime) {
          const startTs = moment(startTime).valueOf();
          const endTs = moment(endTime).valueOf();
          if (!isNaN(startTs) && !isNaN(endTs) && endTs > startTs) {
            const bandColor = normalizedType === 'sleep' ? sleepBandColor :
              (normalizedType === 'activity' || normalizedType === 'workout') ? activityBandColor : undefined;
            if (bandColor) {
              plotBands.push({
                from: startTs,
                to: endTs,
                color: bandColor,
                zIndex: 0
              });
            }
          }
        }

        if (normalizedType === 'sleep') {
          timelinePoint.marker.symbol = 'url(/assets/img/sleepn.png)';
          sleepPoints.push(timelinePoint);
        } else if (normalizedType === 'activity' || normalizedType === 'workout') {
          timelinePoint.marker.symbol = 'url(/assets/img/activityn.png)';
        workoutPoints.push(timelinePoint);
        } else {
          timelinePoint.marker.symbol = 'url(/assets/img/nutritionn.png)';
        otherTimelinePoints.push(timelinePoint);
        }
      });
    }

    // Add timeline scatter series if we have timeline data
    const scatterBaseConfig = {
      type: 'scatter' as const,
      showInLegend: true,
      enableMouseTracking: true,
      stickyTracking: false,
      states: {
        hover: { enabled: false },
        inactive: { enabled: false },
      },
      tooltip: { enabled: false },
    };

    if (sleepPoints.length > 0) {
      seriesData.push({
        name: 'Sleep',
        data: sleepPoints,
        ...scatterBaseConfig,
        marker: {
          symbol: 'url(/assets/img/sleepn.png)',
          width: 18,
          height: 18,
        },
      });
    }

    if (workoutPoints.length > 0) {
      seriesData.push({
        name: 'Workout',
        data: workoutPoints,
        ...scatterBaseConfig,
        marker: {
          symbol: 'url(/assets/img/activityn.png)',
          width: 18,
          height: 18,
        },
      });
    }

    if (otherTimelinePoints.length > 0) {
      seriesData.push({
        name: '',
        data: otherTimelinePoints,
        ...scatterBaseConfig,
        showInLegend: false,
      });
    }
    
    // Determine date range for x-axis based on ALL metric data (not just selected)
    // This ensures x-axis range stays consistent when toggling metrics
    let minTimestamp: number | undefined;
    let maxTimestamp: number | undefined;

    // Get min/max from ALL metric data
    Object.keys(metricConfig).forEach((metric: string) => {
      const config = metricConfig[metric];
      if (!config) return;
      const metricData = this.metricsData[config.dataKey] || [];
      metricData.forEach((item: any) => {
        if (item.timestamp) {
          if (minTimestamp === undefined || item.timestamp < minTimestamp) {
            minTimestamp = item.timestamp;
          }
          if (maxTimestamp === undefined || item.timestamp > maxTimestamp) {
            maxTimestamp = item.timestamp;
          }
        }
      });
    });

    // Get min/max from timeline data
    if (this.timeLineData && this.timeLineData.length > 0) {
      this.timeLineData.forEach((timeline: any) => {
        if (timeline.date) {
          const timestamp = moment(timeline.date).valueOf();
          if (minTimestamp === undefined || timestamp < minTimestamp) {
            minTimestamp = timestamp;
          }
          if (maxTimestamp === undefined || timestamp > maxTimestamp) {
            maxTimestamp = timestamp;
          }
        }
      });
    }

    // Fallback to date range if no data
    if (minTimestamp === undefined || maxTimestamp === undefined) {
      if (this.sDate && this.eDate) {
        minTimestamp = moment(this.sDate).valueOf();
        maxTimestamp = moment(this.eDate).endOf('day').valueOf();
      } else {
        minTimestamp = moment().subtract(7, 'days').valueOf();
        maxTimestamp = moment().valueOf();
      }
    }

    // Calculate the actual data range
    const dataRange = maxTimestamp - minTimestamp;
    
    // For single day or very small ranges, set proper bounds
    // If range is less than 1 day, show the full day with some padding
    if (dataRange < 24 * 60 * 60 * 1000) {
      // Single day or less - show from start of day to end of day
      const startOfDay = moment(minTimestamp).startOf('day').valueOf();
      const endOfDay = moment(maxTimestamp).endOf('day').valueOf();
      minTimestamp = startOfDay;
      maxTimestamp = endOfDay;
    } else {
      // Multiple days - add padding
      minTimestamp = minTimestamp - dataRange * 0.05;
      maxTimestamp = maxTimestamp + dataRange * 0.05;
    }

    // Log timestamp range for debugging

    this.chartOptions = {
      time: {
        useUTC: false // Use local time instead of UTC to match API data timezone
      },
      chart: {
        backgroundColor: '#F9F9F9',
        type: 'line',
        panKey: 'shift',
        panning: {
          enabled: true,
          type: 'x'
        },
        zooming: {
          type: 'x'
        }
      },
      title: { text: this.getChartTitle() },
      xAxis: [{
        type: 'datetime',
        // Set initial min/max to show the data range properly
        min: minTimestamp,
        max: maxTimestamp,
        crosshair: true,
        plotBands: plotBands,
        // Allow deep zoom down to minutes/seconds
        minRange: 60 * 1000, // 1 minute - allows zooming to minute level
        dateTimeLabelFormats: {
          millisecond: '%H:%M:%S.%L',
          second: '%H:%M:%S',
          minute: '%H:%M',
          hour: '%H:%M',
          day: '%b %e',
          week: '%b %e',
          month: '%b \'%y',
          year: '%Y'
        },
        // Scrollbar not available in base Highcharts - use Shift+drag to pan
      }],
      credits: {
        enabled: false
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal'
      },
      yAxis: [{
        lineWidth: 1,
        min: yMin,
        max: yMax,
        title: {
          text: 'Metric Value'
        },
        tickAmount: 8,
        allowDecimals: true
      }],
      tooltip: {
        enabled: true,
        shared: true,
        formatter: function () {
          if (this.points) {
            // Filter out scatter series (timeline icons)
            const validPoints = this.points.filter((point: any) => point.series.name !== '' && point.series.type !== 'scatter');
            if (validPoints.length === 0) {
              return false; // Return false to hide tooltip completely
            }
            
            // Format datetime for tooltip
            const xValue = (this as any).x;
            if (!xValue) return false;
            const date = new Date(xValue);
            const dateStr = moment(date).format('MMM D, YYYY h:mm:ss A');
            let tooltip = '<b>' + dateStr + '</b><br/>';

            validPoints.forEach((point: any) => {
              if (point.y !== null) {
                const roundedValue = Math.round(point.y * 100) / 100;
                tooltip += '<span style="color:' + point.color + '">\u25CF</span> ' +
                  point.series.name + ': <b>' + roundedValue + '</b><br/>';
              }
            });

            return tooltip;
          }
          return false;
        },
        backgroundColor: 'rgba(0,0,0, 0.6)',
        borderWidth: 1,
        shadow: true,
        style: {
          color: '#fff',
          fontSize: '12pt',
          fontFamily: 'Urbanist, sans-serif'
        }
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: false
          },
          enableMouseTracking: true,
          marker: {
            symbol: 'circle',
            enabled: true,
            radius: 3,
            lineWidth: 1
          }
        },
        scatter: {
          // Preserve custom markers (image URLs) for scatter series (Sleep/Activity icons)
          marker: {
            enabled: true
          },
          states: {
            hover: { enabled: false },
            inactive: { enabled: false, opacity: 1 }
          }
        },
        series: {
          enableMouseTracking: true
        }
      },
      series: seriesData
    };

    // Hide loading spinner after chart is created
    this.isLoading = 2;

    // Add chart callback to capture instance for zoom control
    this.chartOptions.chart!.events = {
      load: this.chartCallback
    };
  }

  private setEmptyChart(): void {
    // Determine date range for x-axis
    let minTimestamp: number;
    let maxTimestamp: number;
    
    if (this.sDate && this.eDate) {
      minTimestamp = moment(this.sDate).valueOf();
      maxTimestamp = moment(this.eDate).endOf('day').valueOf();
    } else {
      minTimestamp = moment().subtract(7, 'days').valueOf();
      maxTimestamp = moment().valueOf();
    }

    this.chartOptions = {
      time: {
        useUTC: false // Use local time instead of UTC to match API data timezone
      },
      chart: {
        backgroundColor: '#F9F9F9',
        type: 'line'
      },
      title: { text: this.getChartTitle() },
      xAxis: [{
        type: 'datetime',
        min: minTimestamp,
        max: maxTimestamp,
        gridLineWidth: 0,
        minorGridLineWidth: 0,
        dateTimeLabelFormats: {
          millisecond: '%H:%M:%S.%L',
          second: '%H:%M:%S',
          minute: '%H:%M',
          hour: '%H:%M',
          day: '%b %e',
          week: '%b %e',
          month: '%b \'%y',
          year: '%Y'
        }
      }],
      yAxis: [{
        title: { text: 'Metric Value' },
        lineWidth: 1,
        gridLineWidth: 0,
        minorGridLineWidth: 0
      }],
      credits: { enabled: false },
      legend: { enabled: false },
      series: [{
        type: 'line',
        name: '',
        data: [],
        showInLegend: false,
        enableMouseTracking: true
      }]
    };

    this.updateFlag = true;
    this.showChart = true;
  }

  private getChartTitle(): string {
    const selected = this.getSelectedMetrics();
    if (selected.length === 0) return 'Health Metrics';
    if (selected.length === 1) {
      const metricNames: { [key: string]: string } = {
        'hr': 'Heart Rate',
        'rhr': 'Resting Heart Rate',
        'hrv': 'HRV'
      };
      return metricNames[selected[0]] || 'Health Metrics';
    }
    const metricNames: { [key: string]: string } = {
      'hr': 'Heart Rate',
      'rhr': 'Resting HR',
      'hrv': 'HRV'
    };
    return 'Health Metrics - ' + selected.map(m => metricNames[m]).join(', ');
  }

  private chartCallback = (chart: any) => {
    const actualChart = chart.target;
    this.chartInstance = actualChart;
  }

  private forceChartRecreation(): void {
    this.showChart = false;
    setTimeout(() => {
      this.showChart = true;
    }, 150);
  }

  private enumerateDates(startStr: string, endStr: string): Date[] {
    const start = new Date(startStr);
    const end = new Date(endStr);
    const dates: Date[] = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d));
    }
    return dates;
  }

  showInfo(type: any, data: any) {
    this.showModal = true;
    if (type === 'Activity') {
      this.modalImg = '/assets/img/activityn.png';
      this.modalType = type;
      this.modalSTime = data.startTime ? formatDate(data.startTime, 'hh:mm a', 'en') : '';
      this.modalETime = data.endTime ? formatDate(data.endTime, 'hh:mm a', 'en') : '';
      this.activityName = data.type || '';
      this.calsBurned = data.calsBurned || 0;
      this.distCovered = ((data.distanceCovered || 0) * 1.609).toFixed(1);
      if (
        this.memberbasic?.address?.country?.toUpperCase() === 'UNITED STATES' ||
        this.memberbasic?.address?.country?.toUpperCase() === 'CANADA'
      ) {
        this.distUnits = 'miles';
        this.distCovered = (data.distanceCovered || 0).toString();
      } else {
        this.distUnits = 'Km';
      }
    } else if (type === 'Sleep') {
      this.modalImg = '/assets/img/sleepn.png';
      this.modalType = type;
      this.modalSTime = data.startTime ? formatDate(data.startTime, 'hh:mm a', 'en') : '';
      this.modalETime = data.endTime ? formatDate(data.endTime, 'hh:mm a', 'en') : '';
      const duration = data.duration || 0;
      const hours = Math.floor(duration / 3600);
      const minutes = Math.floor((duration % 3600) / 60);
      this.modalDuration = hours + ' hrs ' + minutes + ' min';
    }
    $('#metricsTimelineModal').modal('show');
  }

  closeModal() {
    $('#metricsTimelineModal').modal('hide');
  }
}
